/**
 * Organisation API client (CUJ-84).
 *
 * Typed mirror of the api-service-go `/locations` and `/roles` endpoints.
 * The backend is the contract authority; these types
 * mirror the wire shapes exactly.
 *
 * Writes carry an `Idempotency-Key` (a fresh UUID per call) so a retried
 * mutation — e.g. a double-tap on "Create" or a network replay — can never
 * create a duplicate unit/role or double-apply a grant. The server stores
 * key → result and replays the cached response.
 *
 * Lists continue through `Link: rel="next"` (RFC 8288), which `GET /locations`
 * sends whenever the DB returned a full page of CANDIDATES (before per-row
 * ACL filtering), so a short visible page can still have more pages behind it.
 * `listAllLocations` follows it to assemble the complete visible set the tree
 * needs.
 */

import { api } from '@/api/client';
import type { Page } from '@/api/types';
import type { PatientResponse } from '@/api/endpoints/patients';
import {
  revokeResourceShareOfKind,
  type ResourceGrant,
  type SharePrincipalType,
} from '@/api/endpoints/sharing';
import { useConfigStore } from '@/stores/config';
import { randomUUID } from '@/lib/uuid';

// --- Wire types (mirror api-service-go) ---------------------------------------

export type OrgRole = 'owner' | 'editor' | 'viewer';
export type PrincipalType = 'user' | 'group';

/**
 * Access tiers in ascending strength, for collapsing a principal's grants.
 *
 * Total over the wire vocabulary: the server maps every role it cannot
 * recognise to `viewer` before serialising (roleToAccessLevelLeastPrivilege,
 * fail-closed), so `access_level` is always one of these three and a lookup
 * here cannot miss.
 */
const ORG_ROLE_RANK: Record<OrgRole, number> = { viewer: 1, editor: 2, owner: 3 };

export interface Location {
  id: string;
  name: string;
  kind: string;
  parent_id?: string;
  labels: Record<string, string>;
  /**
   * Patients admitted DIRECTLY to this unit (its own occupancy) — NOT a rollup.
   * The console composes the subtree census (this unit plus every descendant)
   * client-side from these per-node counts over the visible tree
   * (`tree.rollUpPatientCounts`), so a census never counts — or leaks — a unit
   * outside the caller's access. Present on the list endpoint (always set, or the
   * request fails); absent on the single-location detail, which has no count.
   */
  direct_patient_count?: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * One principal's access to a unit, as the org console renders it.
 *
 * The unit's access lives on the ONE sharing contract every resource uses
 * (`/locations/{id}/shares`); the bespoke `/locations/{id}/members` routes this
 * shape used to come from are gone. `role` is the share's `access_level`: the
 * same owner/editor/viewer vocabulary under the name the console shows.
 */
export interface LocationMember {
  principal_type: PrincipalType;
  principal_id: string;
  role: OrgRole;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  labels: Record<string, string>;
  org_id?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** One member of a role: tricorder-roles keeps a membership as its tuple, so a member is their user id. */
export interface RoleMember {
  user_id: string;
}

export interface RoleGrant {
  resource_type: string;
  resource_id: string;
  role: OrgRole;
}

// --- Request/response envelopes -----------------------------------------------

export interface ListLocationsParams {
  parent_id?: string; // omit = any; "root" = top-level only; <uuid> = children of
  kind?: string;
  limit?: number;
  offset?: number;
}

type ListLocationsResponse = Page<Location>;

interface LocationDetailResponse {
  location: Location;
  ancestors: Location[];
}

interface ListLocationPatientsResponse extends Page<PatientResponse> {
  location_id: string;
}

type ListRolesResponse = Page<Role>;

interface ListRoleMembersResponse extends Page<RoleMember> {
  role_id: string;
}

/**
 * GET /roles/{id}/grants — the one list envelope plus the two facts that are
 * about this collection rather than about paging.
 *
 * `truncated` is NOT `next`: it says at least one per-type fan-out hit
 * org.role_grants_per_type_limit, so the union the page was cut from is itself
 * short of the truth. Neither is derivable from the other.
 */
interface ListRoleGrantsResponse extends Page<RoleGrant> {
  role_id: string;
  truncated: boolean;
}

// --- Helpers ------------------------------------------------------------------

// Idempotency key per write — a caller that needs to reuse the SAME key
// across a client-side retry (e.g. a still-open confirm form) passes one in;
// otherwise a fresh one is minted. randomUUID (lib/uuid) carries the
// RFC4122-v4 fallback for non-secure dev hosts (e.g. http://lvh.me:3000)
// where crypto.randomUUID is absent.
function idem(key?: string): { headers: Record<string, string> } {
  return { headers: { 'Idempotency-Key': key ?? randomUUID() } };
}

function locationsQuery(params: ListLocationsParams = {}): string {
  const q = new URLSearchParams();
  if (params.parent_id !== undefined) q.set('parent_id', params.parent_id);
  if (params.kind) q.set('kind', params.kind);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  if (params.offset !== undefined) q.set('offset', String(params.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

// The cap the org list endpoints clamp `limit` to (app.yaml org.max_page_size,
// served via /config — the SINGLE source; there is deliberately no client-side
// fallback). Paging at exactly the cap minimises round-trips for the full-set
// fetches; the `rel="next"` link carries the limit on to every later page.
// Every walker-based hook is gated on isConfigLoaded, so this
// throwing means a programming error (an ungated caller) or a degraded /config
// org block — either way the query surfaces a loud error state with retry
// instead of silently paging at a wrong cap. Safe outside React (getState),
// same pattern as lib/query-config.ts.
function orgPageSize(): number {
  const cap = useConfigStore.getState().config?.org?.max_page_size;
  if (!cap) {
    throw new Error(
      'org.max_page_size is not available from /config; org full-set walkers must be gated on isConfigLoaded',
    );
  }
  return cap;
}

// --- Client -------------------------------------------------------------------

export const orgApi = {
  // Locations ----------------------------------------------------------------
  listLocations: (params?: ListLocationsParams) =>
    api.getPage<ListLocationsResponse>(`/locations${locationsQuery(params)}`),

  /**
   * Walk every page of the visible location set so the client can fold the flat
   * adjacency list into a tree. Follows `rel="next"` — a short page does NOT
   * signal the end here, because per-row ACL filtering can shrink a page that
   * still has more candidates behind it.
   */
  listAllLocations: (params: Omit<ListLocationsParams, 'limit' | 'offset'> = {}) =>
    api.getAllItems<ListLocationsResponse>(
      `/locations${locationsQuery({ ...params, limit: orgPageSize() })}`,
    ),

  getLocation: (id: string) => api.get<LocationDetailResponse>(`/locations/${id}`),

  createLocation: (body: { name: string; kind: string; parent_id?: string; labels?: Record<string, string> }) =>
    api.post<Location>('/locations', body, idem()),

  updateLocation: (id: string, body: { name?: string; labels?: Record<string, string> }) =>
    api.patch<Location>(`/locations/${id}`, body, idem()),

  // parent_id null detaches to a root; a uuid re-parents. Server rejects cycles
  // and self-parent (400) — the UI surfaces that, it does not pre-validate.
  moveLocation: (id: string, parentId: string | null) =>
    api.put<Location>(`/locations/${id}/parent`, { parent_id: parentId }, idem()),

  deleteLocation: (id: string) => api.delete<void>(`/locations/${id}`, idem()),

  // A unit's access is read, granted and revoked on the resource, through the
  // same three routes documents and sessions use. Only the projection into the
  // console's row shape happens here.
  listLocationMembers: async (id: string): Promise<Page<LocationMember>> => {
    const grants = await api.getAllItems<Page<ResourceGrant>>(`/locations/${id}/shares`);
    // ONE row per principal, at their strongest access. The share contract
    // revokes a PRINCIPAL, not a principal-at-a-role, so a list that showed a
    // person twice would offer two remove buttons that both remove everything.
    // The operator presses one, watches the other row vanish, and cannot tell
    // whether that was the contract or a bug.
    const strongest = new Map<string, LocationMember>();
    for (const g of grants) {
      const key = `${g.principal_type}:${g.principal_id}`;
      const row: LocationMember = {
        principal_type: g.principal_type as PrincipalType,
        principal_id: g.principal_id,
        role: g.access_level as OrgRole,
      };
      const held = strongest.get(key);
      if (!held || ORG_ROLE_RANK[row.role] > ORG_ROLE_RANK[held.role]) {
        strongest.set(key, row);
      }
    }
    // Every page of grants is read first: a principal's strongest grant can
    // sit on a later page, so folding one page would show the weaker one.
    return { items: [...strongest.values()], next: null };
  },

  listLocationPatients: (id: string) =>
    api.getPage<ListLocationPatientsResponse>(`/locations/${id}/patients`),

  addLocationMember: (id: string, body: LocationMember) =>
    api.post<unknown>(
      `/locations/${id}/shares`,
      {
        principal: { type: body.principal_type, id: body.principal_id },
        access_level: body.role,
      },
      idem(),
    ),

  // The revoke is addressed by PRINCIPAL, with no role segment: the share
  // contract removes a principal's access to the resource, which is the intent
  // the console's button states. The old member route took a role and could
  // strip one of several, leaving access the operator believed they had removed.
  removeLocationMember: (id: string, m: LocationMember) =>
    revokeResourceShareOfKind('node', id, m.principal_type as SharePrincipalType, m.principal_id),

  admitPatient: (locationId: string, patientId: string, idempotencyKey?: string) =>
    api.post<unknown>(`/locations/${locationId}/patients`, { patient_id: patientId }, idem(idempotencyKey)),

  dischargePatient: (locationId: string, patientId: string) =>
    api.delete<void>(`/locations/${locationId}/patients/${encodeURIComponent(patientId)}`, idem()),

  // Roles --------------------------------------------------------------------
  listRoles: (params: { limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    const s = q.toString();
    return api.getPage<ListRolesResponse>(`/roles${s ? `?${s}` : ''}`);
  },

  /**
   * Follow the role catalogue's `rel="next"` to its last page, same contract
   * as locations. Without this, the unadorned `GET /roles` silently caps at
   * the server's default page size.
   */
  listAllRoles: () =>
    api.getAllItems<ListRolesResponse>(`/roles?limit=${orgPageSize()}`),

  getRole: (id: string) => api.get<Role>(`/roles/${id}`),

  createRole: (body: { name: string; description?: string; labels?: Record<string, string> }) =>
    api.post<Role>('/roles', body, idem()),

  updateRole: (id: string, body: { name?: string; description?: string; labels?: Record<string, string> }) =>
    api.patch<Role>(`/roles/${id}`, body, idem()),

  deleteRole: (id: string) => api.delete<void>(`/roles/${id}`, idem()),

  listRoleMembers: (id: string) => api.getPage<ListRoleMembersResponse>(`/roles/${id}/members`),

  addRoleMember: (id: string, userId: string) =>
    api.post<unknown>(`/roles/${id}/members`, { user_id: userId }, idem()),

  removeRoleMember: (id: string, userId: string) =>
    api.delete<void>(`/roles/${id}/members/${encodeURIComponent(userId)}`, idem()),

  // A role's grants are the access graph filtered to that principal. They were
  // /roles/{id}/grants, which could only ever answer for a role even though the
  // same fan-out answers for a user or an agent unchanged — the principal was
  // in the path purely to name it. `group` is the graph's word for a role.

  listRoleGrants: (id: string) => {
    const q = new URLSearchParams({ principal_type: 'role', principal_id: id });
    return api.getPage<ListRoleGrantsResponse>(`/grants?${q.toString()}`);
  },

  addRoleGrant: (id: string, grant: RoleGrant) =>
    api.post<unknown>(
      '/grants',
      {
        principal_type: 'role',
        principal_id: id,
        resource_type: grant.resource_type,
        resource_id: grant.resource_id,
        relation: grant.role,
      },
      idem(),
    ),

  removeRoleGrant: (id: string, grant: RoleGrant) => {
    const q = new URLSearchParams({
      principal_type: 'role',
      principal_id: id,
      resource_type: grant.resource_type,
      resource_id: grant.resource_id,
      relation: grant.role,
    });
    return api.delete<void>(`/grants?${q.toString()}`, idem());
  },
};
